<template>
  <div v-if="hideMenu">
    <v-app-bar
      id="top-bar"
      flat
      color="headerPrimary"
      class="p-simple-header-bar d-flex justify-center align-center"
    >
      <v-img contain class="p-logo-simple-header" :src="tenantLogoLight" />
    </v-app-bar>
  </div>

  <div v-else>
    <v-app-bar id="top-bar" flat class="align-center" color="headerPrimary">
      <v-app-bar-nav-icon
        v-if="$vuetifyDisplay.mdAndDown"
        class="mr-n4 mr-sm-2"
        @click="$emit('toggle')"
      ></v-app-bar-nav-icon>

      <v-btn
        v-if="$vuetifyDisplay.smAndUp"
        :to="{ name: 'dashboard' }"
        depressed
      >
        <v-img contain class="p-logo-full-header" :src="tenantLogoLight" />
      </v-btn>

      <template v-else>
        <v-spacer></v-spacer>
        <v-btn :to="{ name: 'dashboard' }" depressed class="ml-12">
          <v-img contain class="p-logo-full-header" :src="tenantLogoLight" />
        </v-btn>
      </template>

      <v-divider
        v-if="$vuetifyDisplay.smAndUp"
        class="mx-3 mx-md-6 hidden-xs-and-down"
        inset
        vertical
      ></v-divider>

      <div v-if="$vuetifyDisplay.mdAndUp">
        <span
          v-if="userGuideEnabled"
          class="p-user-guide"
          :class="{ 'p-user-guide-active': $route.name === 'user-guide' }"
          @click="$router.push({ name: 'user-guide' })"
        >
          USER GUIDE
        </span>
      </div>

      <v-spacer></v-spacer>

      <span v-if="$vuetifyDisplay.lgAndUp" class="p-timezone-indicator">{{
        timezoneDisplayLabel
      }}</span>

      <div v-if="$vuetifyDisplay.mdAndUp">
        <span class="p-supplier-name">
          {{ truncateSupplierName }}
          <span v-if="supplierAccountCode" class="p-supplier-id"
            >Supplier ID: {{ supplierAccountCode }}</span
          >
        </span>
        <template v-if="featureSubscriptionEnabled">
          <br />
          <span
            class="p-supplier-subscription"
            :class="{
              'p-supplier-subscription-gold': subscriptionStatus === 'GOLD',
              'p-supplier-subscription-silver': subscriptionStatus === 'SILVER',
              'p-supplier-subscription-review':
                subscriptionStatus === 'REVIEW_SUBSCRIPTION'
            }"
            @click="$router.push({ name: 'subscription' })"
          >
            {{ subscriptionStatusDisplayName.toUpperCase() }}
          </span>
        </template>
      </div>

      <v-divider
        class="ml-4 ml-md-6 mr-1 mr-md-3 hidden-xs-and-down"
        inset
        vertical
      ></v-divider>

      <v-menu
        transition="slide-y-transition"
        offset-y
        z-index="400"
        content-class="p-account-menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-badge
            class="p-account-alert-badge"
            overlap
            location="top end"
            offset-x="4"
            offset-y="4"
            color="orange"
            :content="actionableConsentsCount"
            :value="actionableConsentsCount"
          >
            <v-btn v-bind="attrs" icon v-on="on" class="mr-0">
              <v-icon medium>mdi-account</v-icon>
            </v-btn>
          </v-badge>
        </template>

        <v-list class="p-account-menu-list">
          <v-list-item :to="{ name: 'account' }">
            <v-badge
              inline
              color="orange"
              :content="actionableConsentsCount"
              :value="actionableConsentsCount"
            >
              <v-list-item-content>
                <v-list-item-title>Manage account</v-list-item-title>
              </v-list-item-content>
            </v-badge>
          </v-list-item>

          <v-list-item
            :to="{ name: 'pick-supplier' }"
            v-if="supplierChangeEnabled"
          >
            <v-list-item-content>
              <v-list-item-title>Change supplier</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="logout">
            <v-list-item-content>
              <v-list-item-title>Log out</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { getUserTimezoneDisplayLabel } from '@/utils/timezone'

const MAX_SUPPLIER_NAME_LENGTH = 25
const MAX_SUPPLIER_NAME_LENGTH_LIMITER = MAX_SUPPLIER_NAME_LENGTH - 2
export default {
  computed: {
    ...mapState('auth', { supplierChangeEnabled: 'multiSupplier' }),
    ...mapState('profile', {
      name: (state) => state.info?.name,
      supplierAccountCode: (state) => state.info?.externalAccountCode
    }),

    ...mapGetters('tenant', [
      'featureSubscriptionEnabled',
      'tenantLogoLight',
      'userGuideEnabled'
    ]),
    ...mapGetters('profile', [
      'subscriptionStatus',
      'subscriptionStatusDisplayName'
    ]),
    ...mapGetters('retailerInvites', ['actionableConsents']),

    hideMenu() {
      return this.$route.meta?.hideMenu
    },

    truncateSupplierName() {
      if (this.$vuetifyDisplay.smAndUp) {
        return this.name
      } else {
        return this.name?.length > MAX_SUPPLIER_NAME_LENGTH
          ? this.name.substr(0, MAX_SUPPLIER_NAME_LENGTH_LIMITER) + '...'
          : this.name
      }
    },

    actionableConsentsCount() {
      return this.actionableConsents?.length || 0
    },

    timezoneDisplayLabel() {
      return getUserTimezoneDisplayLabel()
    }
  },

  methods: {
    ...mapActions('auth', ['logout'])
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#top-bar {
  background-color: rgb(var(--v-theme-headerPrimary, 0, 0, 0)) !important;

  :deep(.v-toolbar__content) {
    padding: 4px 16px;
  }

  &.p-simple-header-bar {
    :deep(.v-toolbar__content) {
      justify-content: center;
    }
  }

  // Vuetify's app-bar-nav-icon/menu/notification icons are rendered deep
  // inside Vuetify's own internal component markup (via an `icon` prop, not
  // slot content this component authors itself), so they never receive this
  // component's `data-v-*` scope attribute. Without :deep(), this rule
  // silently never matches and these icons fall back to the app-wide
  // default (a dark, low-emphasis grey meant for light backgrounds - see
  // "i.mdi:not(.v-icon)" in _vuetify-legacy.scss), making them invisible
  // against this header's dark background.
  :deep(.mdi) {
    color: white !important;
    font-size: 38px !important;
    height: 24px;
    min-width: auto;
    width: 24px;
  }
  .p-logo {
    &-simple-header,
    &-full-header {
      height: 42px;
      width: 175px;
    }
  }
  .p-supplier-name {
    color: white;
    font-weight: bold;
    letter-spacing: 0.75px;
    font-size: 14px;
  }
  .p-supplier-id {
    display: block;
    font-size: 12px;
    font-weight: normal;
    white-space: nowrap;
  }
  .p-supplier-subscription {
    background-color: $secondary-color;
    border-radius: 3px;
    padding-left: 5px;
    padding-right: 5px;
    color: white;
    font-weight: bold;
    letter-spacing: 0.75px;
    font-size: 12px;
    &:hover {
      cursor: pointer;
    }
    &-gold {
      background-color: darkgoldenrod !important;
    }
    &-silver {
      background-color: darkgray !important;
    }
    &-review {
      background-color: $red-color !important;
    }
  }
  .p-user-guide {
    color: white;
    font-weight: bold;
    letter-spacing: 0.75px;
    font-size: 12px;
    padding: 2px 5px;
    border: 1px solid white;
    border-radius: 3px;
    &:hover {
      cursor: pointer;
      background-color: rgba(255, 255, 255, 0.15);
    }
    &-active {
      background-color: rgba(255, 255, 255, 0.15);
    }
  }
  .p-timezone-indicator {
    color: white;
    font-size: 12px;
    font-weight: 500;
    margin: 0 12px;
    white-space: nowrap;
  }
  .v-btn {
    background-color: transparent;
    color: transparent;
  }
  .v-btn--icon:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
  .v-btn:not(.v-btn--icon) {
    padding: 0;
    min-width: 0;
  }
  :deep(.p-account-alert-badge .v-badge__badge) {
    align-items: center;
    border-radius: 9px;
    display: inline-flex;
    font-size: 11px !important;
    height: 18px;
    justify-content: center;
    line-height: 18px;
    min-width: 18px;
    padding: 0 6px;
    right: -4px;
    top: -4px;
  }
  .v-divider {
    border-color: rgba(255, 255, 255, 0.23);
    opacity: 1;
  }
}

:deep(.p-account-menu) {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

:deep(.p-account-menu-list) {
  background-color: rgb(var(--v-theme-headerPrimary, 0, 0, 0)) !important;
  color: white;
  padding: 6px 0;
}

:deep(.p-account-menu-list .v-list-item) {
  min-height: 44px;
}

:deep(.p-account-menu-list .v-list-item-title) {
  color: white !important;
}

:deep(.p-account-menu-list .v-list-item__overlay),
:deep(.p-account-menu-list .v-list-item__underlay) {
  background: rgba(255, 255, 255, 0.08);
}

@media only screen and (min-width: 960px) {
  #top-bar {
    .p-logo {
      &-simple-header,
      &-full-header {
        height: 48px;
        width: 175px;
      }
    }
    .p-supplier-name {
      font-size: 16px;
    }
  }
}
</style>
